import type { MapTheme, Tile } from "@dailywars/core/engine";
import { DEFAULT_MAP_THEME } from "@dailywars/core/render/mapThemes";
import { resolveRoadAutotileMask } from "@dailywars/core/render/roadConnectivity";
import {
  classifyRoadAutotile,
  type RoadAutotileKind,
} from "@dailywars/core/render/roadStripeKinds";

import { loadCachedImage } from "#/game/cachedImage";

const ROAD_AUTOTILE_KINDS = [
  "straight",
  "corner",
  "tjunction",
  "crossing",
  "deadend",
] as const satisfies readonly RoadAutotileKind[];

function roadAutotileUrl({ theme, kind }: { theme: MapTheme; kind: RoadAutotileKind }): string {
  return `/terrain/${theme}/road/${kind}.png`;
}

export function getRoadAutotileImageSrc({
  theme,
  kind,
}: {
  theme?: MapTheme;
  kind: RoadAutotileKind;
}): string {
  return roadAutotileUrl({ theme: theme ?? DEFAULT_MAP_THEME, kind });
}

function getRoadAutotileImage({
  theme,
  kind,
}: {
  theme?: MapTheme;
  kind: RoadAutotileKind;
}): HTMLImageElement {
  return loadCachedImage(getRoadAutotileImageSrc({ theme, kind }));
}

export function preloadRoadAutotileImages({ theme }: { theme?: MapTheme }): HTMLImageElement[] {
  return ROAD_AUTOTILE_KINDS.map((kind) => getRoadAutotileImage({ theme, kind }));
}

export function resolveRoadAutotile({
  tiles,
  width,
  x,
  y,
  theme,
}: {
  tiles: readonly Tile[];
  width: number;
  x: number;
  y: number;
  theme?: MapTheme;
}): { img: HTMLImageElement; rotation: number } {
  const mask = resolveRoadAutotileMask({ tiles, width, x, y });
  const { kind, rotation } = classifyRoadAutotile(mask);
  return { img: getRoadAutotileImage({ theme, kind }), rotation };
}
